import axios from 'axios';
import store from '../rtk/store/store';
import { setTokens, clearAuth } from '../rtk/slices/authSlice';

const baseurl =
    process.env.REACT_APP_BASE_URL ||
    "http://localhost:8000/api";

// JWT access tokens expire after 1 hour (see backend SIMPLE_JWT settings).
// Without this, every request made after expiry fails with "Given token
// not valid for any token type" and pages silently render empty, since no
// thunk anywhere previously attempted to use the (7-day) refresh token.

let isRefreshing = false;
let pendingRequests = [];

// Routes reachable without a session. A 401 from a background/optional call
// made on one of these (e.g. a component that fetches user-specific data
// without first checking auth) must not force-navigate a plain visitor away.
const PUBLIC_PATHS = ['/', '/sign-in', '/sign-up', '/verify-email', '/forgot-password', '/terms', '/privacy'];

const processQueue = (error, token = null) => {
    pendingRequests.forEach(({ resolve, reject }) => {
        if (error) reject(error);
        else resolve(token);
    });
    pendingRequests = [];
};

const logoutAndRedirect = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    store.dispatch(clearAuth());
    if (!PUBLIC_PATHS.includes(window.location.pathname)) {
        window.location.href = '/sign-in';
    }
};

export function setupAxiosInterceptors() {
    axios.interceptors.request.use((config) => {
        let timeZone = "UTC";
        try {
            timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
        } catch (error) {
            timeZone = "UTC";
        }
        if (config.headers && typeof config.headers.set === "function") {
            config.headers.set("X-Timezone", timeZone);
        } else {
            config.headers = config.headers || {};
            config.headers["X-Timezone"] = timeZone;
        }
        return config;
    });

    axios.interceptors.response.use(
        (response) => response,
        (error) => {
            const originalRequest = error.config;
            const status = error.response?.status;
            const url = originalRequest?.url || '';

            const isAuthEndpoint =
                url.includes('/token/refresh/') ||
                url.includes('/accounts/login/') ||
                url.includes('/accounts/register/');

            if (status !== 401 || originalRequest?._retry || isAuthEndpoint || !originalRequest) {
                return Promise.reject(error);
            }

            const refreshToken =
                store.getState()?.auth?.refresh_token || localStorage.getItem('refresh_token');

            if (!refreshToken) {
                logoutAndRedirect();
                return Promise.reject(error);
            }

            originalRequest._retry = true;

            if (isRefreshing) {
                return new Promise((resolve, reject) => {
                    pendingRequests.push({ resolve, reject });
                }).then((newAccessToken) => {
                    originalRequest.headers = {
                        ...originalRequest.headers,
                        Authorization: `Bearer ${newAccessToken}`,
                    };
                    return axios(originalRequest);
                });
            }

            isRefreshing = true;

            return axios
                .post(`${baseurl}/accounts/token/refresh/`, { refresh: refreshToken })
                .then((response) => {
                    const { access, refresh } = response.data;

                    localStorage.setItem('access_token', access);
                    if (refresh) {
                        localStorage.setItem('refresh_token', refresh);
                    }
                    store.dispatch(setTokens({ access, refresh }));

                    processQueue(null, access);

                    originalRequest.headers = {
                        ...originalRequest.headers,
                        Authorization: `Bearer ${access}`,
                    };
                    return axios(originalRequest);
                })
                .catch((refreshError) => {
                    processQueue(refreshError, null);
                    logoutAndRedirect();
                    return Promise.reject(refreshError);
                })
                .finally(() => {
                    isRefreshing = false;
                });
        }
    );
}
